/* nimbus-motion-modules.js
 * Per-section creative GSAP factories — Sugar Browed editorial beauty motion.
 *
 * Activated by: <root data-motion-section="<name>">
 * Each factory composes 2-3 primitives from NM.reveals + section-specific moves.
 * Mobile = lighter version of the same concept (never disabled wholesale).
 */
(function () {
  'use strict';

  const NM = window.NimbusMotion;
  if (!NM) return;

  const MM = NM.MM;
  const R  = () => NM.reveals;

  const MOBILE = MM.motionMobile;
  const WIDE   = MM.motionWide;
  const ALL    = `${MM.motionMobile}, ${MM.motionTablet}, ${MM.motionDesktop}`;

  /* Above-fold check — for hero/PDP gallery that load already in viewport */
  function aboveFold(el) {
    return el.getBoundingClientRect().top < window.innerHeight - 80;
  }

  /* Default ScrollTrigger (once) */
  function st(trigger, start) {
    return { trigger, start: start || 'top 82%', toggleActions: 'play none none none', once: true };
  }

  /* ═══════════════════════════════════════════════════════════════
   * HERO — cinematic intro: word-mask heading, letter-track eyebrow,
   * scale-settle background, scale-pop CTA. Sequenced timeline.
   * ═══════════════════════════════════════════════════════════════ */
  NM.register('hero', function (root, mm) {
    const eyebrow = root.querySelector('.sb-eyebrow, .nb-eyebrow-label, [data-hero-eyebrow]');
    const heading = root.querySelector('.sb-h1, .nimbus-heading, h1');
    const lede    = root.querySelector('.sb-lede, .sb-hero__sub, .sb-hero__content p');
    const cta     = root.querySelector('.sb-hero__ctas, .sb-hero__cta, .button, a[class*="button"]');
    const media   = root.querySelector('.sb-hero__media, [data-hero-media]');
    const above   = aboveFold(root);

    mm.add(MM.reduced, () => {});

    mm.add(ALL, () => {
      const tl = gsap.timeline({
        scrollTrigger: above ? null : st(root, 'top 90%'),
        delay: above ? 0.15 : 0
      });

      if (media) R().imageReveal(media, { duration: 1.6, counter: true });

      if (eyebrow) R().letterTrack(eyebrow, { timeline: tl, position: 0.05, duration: 1.0, from: '0.4em' });

      if (heading) {
        const wm = R().wordMask(heading, { timeline: tl, position: 0.15, duration: 1.0, stagger: 0.07 });
      }

      if (lede) {
        gsap.set(lede, { opacity: 0, y: 18 });
        tl.to(lede, { opacity: 1, y: 0, duration: 0.85, ease: 'power3.out' }, '-=0.55');
      }

      if (cta) R().scalePop(cta, { timeline: tl, position: '-=0.45', duration: 0.7 });
    });

    /* Subtle background scale-out on scroll past hero */
    mm.add(WIDE, () => {
      if (!media) return;
      const inner = media.querySelector('img, picture, video') || media;
      gsap.to(inner, {
        scale: 1.08,
        ease: 'none',
        scrollTrigger: {
          trigger: root,
          start: 'top top',
          end: 'bottom top',
          scrub: 0.6
        }
      });
    });
  });

  /* ═══════════════════════════════════════════════════════════════
   * BRAND-STORY — editorial: image-reveal full bleed, then sticky-ish
   * text reveal stages. Mobile collapses to non-sticky cascade.
   * ═══════════════════════════════════════════════════════════════ */
  NM.register('brand-story', function (root, mm) {
    const media   = root.querySelector('.nimbus-brand-story__media, [data-anim-order="1"]');
    const eyebrow = root.querySelector('.sb-eyebrow, .nimbus-eyebrow');
    const heading = root.querySelector('.nimbus-brand-story__heading, .nimbus-heading, h2');
    const body    = root.querySelector('.nimbus-brand-story__body');
    const quote   = root.querySelector('.nimbus-brand-story__quote');
    const stats   = root.querySelectorAll('.nimbus-brand-story__stats > *');
    const cta     = root.querySelector('.nimbus-brand-story__cta');

    mm.add(MM.reduced, () => {});

    mm.add(ALL, () => {
      if (media)   R().imageReveal(media, { duration: 1.5, counter: true });
      if (eyebrow) R().letterTrack(eyebrow, { duration: 1.0, from: '0.4em' });
      if (heading) R().wordMask(heading, { duration: 1.0, stagger: 0.06 });

      if (body) {
        gsap.set(body, { opacity: 0, y: 24 });
        gsap.to(body, {
          opacity: 1, y: 0, duration: 0.9, ease: 'power3.out',
          scrollTrigger: st(body, 'top 88%')
        });
      }
      if (quote) {
        gsap.set(quote, { opacity: 0, x: -28 });
        gsap.to(quote, {
          opacity: 1, x: 0, duration: 1.0, ease: 'expo.out',
          scrollTrigger: st(quote, 'top 88%')
        });
      }
      if (stats.length) R().staggerDepth([...stats], { trigger: stats[0].parentElement, stagger: 0.1 });
      if (cta) R().scalePop(cta, { start: 'top 88%' });
    });
  });

  /* ═══════════════════════════════════════════════════════════════
   * IMAGE-TEXT — signature editorial pairing.
   * Image: clip-path mask + counter-parallax inner.
   * Heading: word-mask. Eyebrow: letter-track. Stats: countUp + scale-pop.
   * Section number watermark drifts opposite to scroll.
   * ═══════════════════════════════════════════════════════════════ */
  /* ═══════════════════════════════════════════════════════════════
   * COURSE-SPOTLIGHT — editorial split. Heading word-mask, list
   * cascades from left, image clip-reveal, footer scale-pop.
   * ═══════════════════════════════════════════════════════════════ */
  /* ═══════════════════════════════════════════════════════════════
   * IMAGE-TEXT-LIKE — academy-teaser. Same vocab, different selectors.
   * ═══════════════════════════════════════════════════════════════ */
  NM.register('academy-teaser', function (root, mm) {
    const eyebrow = root.querySelector('.sb-eyebrow');
    const heading = root.querySelector('.sb-h1, .sb-academy h2, h2');
    const lede    = root.querySelector('.sb-lede');
    const list    = root.querySelectorAll('.sb-academy__list-item');
    const actions = root.querySelector('.sb-academy__actions');
    const media   = root.querySelector('.sb-academy__media-wrap, .sb-academy__media');

    mm.add(MM.reduced, () => {});

    mm.add(ALL, () => {
      if (media)   R().imageReveal(media, { duration: 1.6, counter: true });
      if (eyebrow) R().letterTrack(eyebrow, { duration: 1.0, from: '0.45em' });
      if (heading) R().wordMask(heading, { duration: 1.05, stagger: 0.07 });

      if (lede) {
        gsap.set(lede, { opacity: 0, y: 16 });
        gsap.to(lede, {
          opacity: 1, y: 0, duration: 0.85, ease: 'power3.out', delay: 0.1,
          scrollTrigger: st(lede, 'top 85%')
        });
      }

      if (list.length) {
        gsap.set([...list], { opacity: 0, x: -24 });
        gsap.to([...list], {
          opacity: 1, x: 0, duration: 0.7, stagger: 0.08, ease: 'power3.out',
          scrollTrigger: st(list[0].parentElement, 'top 84%')
        });
      }

      if (actions) R().scalePop(actions, { duration: 0.7, from: 0.92 });
    });
  });

  /* ═══════════════════════════════════════════════════════════════
   * COLLECTION-GRID — heading clip-path, cards stagger-depth + tilt.
   * ═══════════════════════════════════════════════════════════════ */
  NM.register('collection-grid', function (root, mm) {
    const eyebrow = root.querySelector('.nimbus-eyebrow, .sb-eyebrow');
    const heading = root.querySelector('.nimbus-heading, h2');
    const cards   = root.querySelectorAll('.nimbus-collection-card');

    mm.add(MM.reduced, () => {});

    mm.add(ALL, () => {
      if (eyebrow) R().letterTrack(eyebrow, { duration: 0.95, from: '0.4em' });
      if (heading) R().wordMask(heading, { duration: 1.0, stagger: 0.07 });
      if (cards.length) R().staggerDepth([...cards], { stagger: 0.09, y: 60 });
    });

    mm.add(WIDE, () => {
      const cleanups = [...cards]
        .map(card => R().magneticTilt(card, { max: 5, lift: 3 }))
        .filter(Boolean);
      return () => cleanups.forEach(fn => fn());
    });
  });

  /* ═══════════════════════════════════════════════════════════════
   * FEATURED-PRODUCTS — compact, snappy.
   * ═══════════════════════════════════════════════════════════════ */
  NM.register('featured-products', function (root, mm) {
    /* iOS-safe: opacity + y only — no wordMask, no staggerDepth with rotation/scale.
     * Mobile: header + cards fade in with gentle stagger.
     * Desktop: header slightly delayed, cards stagger up with more depth. */

    const eyebrow = root.querySelector('.sb-eyebrow');
    const heading = root.querySelector('.sb-h2, h2');
    const lede    = root.querySelector('.sb-lede, [data-anim-order="3"]');
    const link    = root.querySelector('.sb-link, [data-anim-order="4"]');
    const cards   = [...root.querySelectorAll('.nimbus-product-card')];

    mm.add(MM.reduced, () => {});

    /* ── Mobile ── */
    mm.add(MOBILE, () => {
      const headerEls = [eyebrow, heading, lede, link].filter(Boolean);

      if (headerEls.length) {
        gsap.set(headerEls, { opacity: 0, y: 16 });
        gsap.to(headerEls, {
          opacity: 1, y: 0,
          duration: 0.7,
          stagger: 0.07,
          ease: 'expo.out',
          scrollTrigger: { trigger: root, start: 'top 95%', once: true }
        });
      }

      if (cards.length) {
        gsap.set(cards, { opacity: 0, y: 24 });
        gsap.to(cards, {
          opacity: 1, y: 0,
          duration: 0.65,
          stagger: 0.07,
          ease: 'expo.out',
          scrollTrigger: {
            trigger: cards[0].parentElement || root,
            start: 'top 95%',
            once: true
          }
        });
      }
    });

    /* ── Desktop/tablet ── */
    mm.add(WIDE, () => {
      const headerEls = [eyebrow, heading, lede, link].filter(Boolean);

      if (headerEls.length) {
        gsap.set(headerEls, { opacity: 0, y: 22 });
        gsap.to(headerEls, {
          opacity: 1, y: 0,
          duration: 0.85,
          stagger: 0.09,
          ease: 'expo.out',
          scrollTrigger: { trigger: root, start: 'top 88%', once: true }
        });
      }

      if (cards.length) {
        const grid = cards[0].parentElement || root;
        gsap.set(cards, { opacity: 0, y: 44 });
        gsap.to(cards, {
          opacity: 1, y: 0,
          duration: 0.8,
          stagger: 0.09,
          ease: 'expo.out',
          scrollTrigger: { trigger: grid, start: 'top 88%', once: true }
        });
      }
    });
  });

  /* ═══════════════════════════════════════════════════════════════
   * TESTIMONIALS — eyebrow track, heading word-mask, slider 3D entry.
   * ═══════════════════════════════════════════════════════════════ */
  NM.register('testimonials', function (root, mm) {
    const eyebrow = root.querySelector('.nb-eyebrow-label, .sb-eyebrow, .nimbus-eyebrow');
    const heading = root.querySelector('.sb-h2, .nimbus-heading, h2');
    const stage   = root.querySelector('.nimbus-testimonials__stage, nimbus-testimonials');
    const cards   = root.querySelectorAll('.nimbus-testimonial');

    mm.add(MM.reduced, () => {});

    mm.add(ALL, () => {
      if (eyebrow) R().letterTrack(eyebrow, { duration: 1.0, from: '0.45em' });
      if (heading) R().wordMask(heading, { duration: 1.0, stagger: 0.06 });

      if (stage && cards.length) {
        gsap.set([...cards], { opacity: 0, y: 36, rotationY: -6, transformPerspective: 1000 });
        gsap.to([...cards], {
          opacity: 1, y: 0, rotationY: 0, duration: 1.0, stagger: 0.1,
          ease: 'expo.out',
          scrollTrigger: st(stage, 'top 85%')
        });
      }
    });
  });

  /* ═══════════════════════════════════════════════════════════════
   * NEWSLETTER — heading word-mask, form clip-reveal from left.
   * ═══════════════════════════════════════════════════════════════ */
  NM.register('newsletter', function (root, mm) {
    const eyebrow = root.querySelector('.nimbus-eyebrow, .sb-eyebrow');
    const heading = root.querySelector('.nimbus-newsletter__heading, h2');
    const desc    = root.querySelector('.nimbus-newsletter__description');
    const form    = root.querySelector('.nimbus-newsletter__form-wrap');
    const dis     = root.querySelector('.nimbus-newsletter__disclaimer');

    mm.add(MM.reduced, () => {});

    mm.add(ALL, () => {
      if (eyebrow) R().letterTrack(eyebrow, { duration: 0.95, from: '0.4em' });
      if (heading) R().wordMask(heading, { duration: 1.05, stagger: 0.07 });

      if (desc) {
        gsap.set(desc, { opacity: 0, y: 14 });
        gsap.to(desc, {
          opacity: 1, y: 0, duration: 0.8, ease: 'power3.out', delay: 0.1,
          scrollTrigger: st(desc, 'top 86%')
        });
      }

      if (form) {
        gsap.set(form, { clipPath: 'inset(0 100% 0 0)' });
        gsap.to(form, {
          clipPath: 'inset(0 0% 0 0)', duration: 1.1, ease: 'expo.out',
          delay: 0.2,
          scrollTrigger: st(form, 'top 84%')
        });
      }

      if (dis) {
        gsap.set(dis, { opacity: 0 });
        gsap.to(dis, {
          opacity: 1, duration: 0.6, ease: 'power2.out', delay: 0.5,
          scrollTrigger: st(dis, 'top 90%')
        });
      }
    });
  });

  /* ═══════════════════════════════════════════════════════════════
   * CTA — bold block. Eyebrow track + heading word-mask + button pop.
   * ═══════════════════════════════════════════════════════════════ */
  NM.register('cta', function (root, mm) {
    const eyebrow = root.querySelector('.nimbus-eyebrow, .sb-eyebrow');
    const heading = root.querySelector('.nimbus-heading, h2');
    const lede    = root.querySelector('.nimbus-lede, p');
    const btn     = root.querySelector('.nimbus-btn, .sb-btn, a[class*="btn"]');

    mm.add(MM.reduced, () => {});
    mm.add(ALL, () => {
      if (eyebrow) R().letterTrack(eyebrow, { duration: 0.95, from: '0.4em' });
      if (heading) R().wordMask(heading, { duration: 1.05, stagger: 0.07 });
      if (lede) {
        gsap.set(lede, { opacity: 0, y: 14 });
        gsap.to(lede, {
          opacity: 1, y: 0, duration: 0.8, ease: 'power3.out', delay: 0.1,
          scrollTrigger: st(lede, 'top 85%')
        });
      }
      if (btn) R().scalePop(btn, { duration: 0.7, from: 0.9 });
    });
  });

  /* ═══════════════════════════════════════════════════════════════
   * FAQ (PDP) — heading word-mask, items cascade from left.
   * ═══════════════════════════════════════════════════════════════ */
  NM.register('faq', function (root, mm) {
    const eyebrow = root.querySelector('.nfaq__eyebrow');
    const heading = root.querySelector('.nfaq__heading, h2');
    const items   = root.querySelectorAll('.nfaq__item');
    const cta     = root.querySelector('.nfaq__cta-wrap');

    mm.add(MM.reduced, () => {});
    mm.add(ALL, () => {
      if (eyebrow) R().letterTrack(eyebrow, { duration: 0.95, from: '0.4em' });
      if (heading) R().wordMask(heading, { duration: 1.0, stagger: 0.07 });

      if (items.length) {
        gsap.set([...items], { opacity: 0, x: -28 });
        gsap.to([...items], {
          opacity: 1, x: 0, duration: 0.7, stagger: 0.06, ease: 'power3.out',
          scrollTrigger: st(items[0].parentElement, 'top 86%')
        });
      }

      if (cta) R().scalePop(cta, { duration: 0.65, from: 0.92 });
    });
  });

  /* ═══════════════════════════════════════════════════════════════
   * PDP-SOCIAL-PROOF — pill scale-pop, count-up numbers, cards depth.
   * ═══════════════════════════════════════════════════════════════ */
  NM.register('pdp-social-proof', function (root, mm) {
    const pill    = root.querySelector('.sb-proof-dark__stats-pill');
    const heading = root.querySelector('.sb-proof-dark__heading');
    const sub     = root.querySelector('.sb-proof-dark__sub');
    const cards   = root.querySelectorAll('.sb-proof-dark__card');
    const cta     = root.querySelector('.sb-btn');

    mm.add(MM.reduced, () => {});
    mm.add(ALL, () => {
      if (pill)    R().scalePop(pill, { duration: 0.85, from: 0.9 });
      if (heading) R().wordMask(heading, { duration: 1.05, stagger: 0.07 });

      if (sub) {
        gsap.set(sub, { opacity: 0, y: 14 });
        gsap.to(sub, {
          opacity: 1, y: 0, duration: 0.8, ease: 'power3.out', delay: 0.1,
          scrollTrigger: st(sub, 'top 86%')
        });
      }

      if (cards.length) R().staggerDepth([...cards], { stagger: 0.1, y: 40 });
      if (cta) R().scalePop(cta, { duration: 0.7, from: 0.92 });
    });
  });

  /* ═══════════════════════════════════════════════════════════════
   * PDP-RELATED — polaroid stack: cards rotate-into-place + stagger.
   * ═══════════════════════════════════════════════════════════════ */
  NM.register('pdp-related', function (root, mm) {
    const eyebrow = root.querySelector('.sb-related__eyebrow');
    const heading = root.querySelector('.sb-related__heading');
    const cards   = root.querySelectorAll('.sb-polaroid');
    const cta     = root.querySelector('.sb-btn');

    mm.add(MM.reduced, () => {});
    mm.add(ALL, () => {
      if (eyebrow) R().letterTrack(eyebrow, { duration: 0.95, from: '0.4em' });
      if (heading) R().wordMask(heading, { duration: 1.05, stagger: 0.07 });

      if (cards.length) {
        /* read each card's final rotation from CSS var --rot (or inline) */
        const finals = [...cards].map(c => {
          const m = (c.style.cssText || '').match(/--rot:\s*(-?\d+(?:\.\d+)?)deg/);
          return m ? parseFloat(m[1]) : 0;
        });
        cards.forEach((c, i) => {
          gsap.set(c, { opacity: 0, y: 60, rotation: 0, scale: 0.85 });
        });
        cards.forEach((c, i) => {
          gsap.to(c, {
            opacity: 1, y: 0, rotation: finals[i], scale: 1,
            duration: 1.0, ease: 'expo.out',
            delay: i * 0.12,
            scrollTrigger: st(c.parentElement, 'top 80%')
          });
        });
      }

      if (cta) R().scalePop(cta, { duration: 0.7, from: 0.92 });
    });
  });

  /* ═══════════════════════════════════════════════════════════════
   * PRODUCT-HERO (PDP main) — cinematic: gallery image-reveal,
   * info column gets staggered word-mask title + scale-pop price.
   * ═══════════════════════════════════════════════════════════════ */
  NM.register('product-hero', function (root, mm) {
    const gallery = root.querySelector('.nmpd-gallery, .nb-product-hero__media');
    const stage   = gallery && (gallery.querySelector('.nmpd-gallery__stage') || gallery);
    const info    = root.querySelector('.nmpd-info, .nb-product-hero__content, .sb-hero__content');
    const vendor  = info && info.querySelector('.nmpd-vendor, .nb-product-hero__vendor, .sb-eyebrow');
    const title   = info && info.querySelector('.nmpd-title, .nb-product-hero__title, h1');
    const price   = info && info.querySelector('.nmpd-price, .price, [class*="price"]');
    const desc    = info && info.querySelector('.nmpd-desc, .nb-product-hero__desc, .product__description');
    const atc     = info && info.querySelector('.nmpd-atc, [type="submit"]');
    const above   = aboveFold(root);

    mm.add(MM.reduced, () => {});

    mm.add(ALL, () => {
      const tl = gsap.timeline({
        scrollTrigger: above ? null : st(root, 'top 90%'),
        delay: above ? 0.1 : 0
      });

      if (stage) {
        gsap.set(stage, { clipPath: 'inset(8% 8% 8% 8%)', opacity: 0 });
        tl.to(stage, {
          clipPath: 'inset(0% 0% 0% 0%)', opacity: 1,
          duration: 1.4, ease: 'expo.out'
        }, 0);
      }

      if (vendor) R().letterTrack(vendor, { timeline: tl, position: 0.3, duration: 0.9, from: '0.4em' });
      if (title)  R().wordMask(title, { timeline: tl, position: 0.4, duration: 0.95, stagger: 0.06 });
      if (price)  R().scalePop(price, { timeline: tl, position: '-=0.4', duration: 0.65, from: 0.85 });

      if (desc) {
        gsap.set(desc, { opacity: 0, y: 14 });
        tl.to(desc, { opacity: 1, y: 0, duration: 0.7, ease: 'power3.out' }, '-=0.3');
      }

      if (atc) R().scalePop(atc, { timeline: tl, position: '-=0.25', duration: 0.6, from: 0.92 });
    });
  });

  /* ═══════════════════════════════════════════════════════════════
   * RICH-TEXT — block-by-block word-mask cascade.
   * ═══════════════════════════════════════════════════════════════ */
  NM.register('rich-text', function (root, mm) {
    const blocks = root.querySelectorAll('.nimbus-rich__heading, .nimbus-rich__text, .nimbus-rich__btn-row, .nimbus-rich__divider, .nimbus-eyebrow');

    mm.add(MM.reduced, () => {});
    mm.add(ALL, () => {
      blocks.forEach((el, i) => {
        if (el.matches('.nimbus-rich__heading')) {
          R().wordMask(el, { duration: 1.0, stagger: 0.06, start: 'top 86%' });
        } else {
          gsap.set(el, { opacity: 0, y: 18 });
          gsap.to(el, {
            opacity: 1, y: 0, duration: 0.75, ease: 'power3.out',
            delay: i * 0.05,
            scrollTrigger: st(el, 'top 88%')
          });
        }
      });
    });
  });

  /* ═══════════════════════════════════════════════════════════════
   * BEFORE-AFTER — cinematic image-reveal of the comparison frame.
   * ═══════════════════════════════════════════════════════════════ */
  NM.register('before-after', function (root, mm) {
    mm.add(MM.reduced, () => {});
    mm.add(ALL, () => {
      R().imageReveal(root, { duration: 1.5, counter: false, start: 'top 80%' });
    });
  });

  /* ═══════════════════════════════════════════════════════════════
   * MARQUEE — fade container, CSS handles the loop.
   * ═══════════════════════════════════════════════════════════════ */
  NM.register('marquee', function (root, mm) {
    mm.add(MM.reduced, () => {});
    mm.add(ALL, () => {
      gsap.set(root, { opacity: 0 });
      gsap.to(root, {
        opacity: 1, duration: 1.0, ease: 'power2.out',
        scrollTrigger: st(root, 'top 90%')
      });
    });
  });

  /* ═══════════════════════════════════════════════════════════════
   * COLLECTION-BANNER — fallback to image-text-style reveal.
   * ═══════════════════════════════════════════════════════════════ */
  NM.register('collection-banner', function (root, mm) {
    const eyebrow = root.querySelector('.nimbus-eyebrow, .sb-eyebrow');
    const heading = root.querySelector('.nimbus-heading, h2');
    const body    = root.querySelector('p, .nimbus-collection-banner__body');
    const cta     = root.querySelector('.sb-btn, .nimbus-btn, a[class*="btn"]');

    mm.add(MM.reduced, () => {});
    mm.add(ALL, () => {
      if (eyebrow) R().letterTrack(eyebrow, { duration: 0.95, from: '0.4em' });
      if (heading) R().wordMask(heading, { duration: 1.0, stagger: 0.07 });
      if (body) {
        gsap.set(body, { opacity: 0, y: 16 });
        gsap.to(body, {
          opacity: 1, y: 0, duration: 0.8, ease: 'power3.out', delay: 0.1,
          scrollTrigger: st(body, 'top 86%')
        });
      }
      if (cta) R().scalePop(cta, { duration: 0.7, from: 0.92 });
    });
  });

  /* ═══════════════════════════════════════════════════════════════
   * EBOOK-PREVIEW — cover scale + content reveal.
   * ═══════════════════════════════════════════════════════════════ */
  NM.register('ebook-preview', function (root, mm) {
    const cover = root.querySelector('.nb-ebook__cover, [class*="ebook__cover"]');
    const eyebrow = root.querySelector('.sb-eyebrow, .nimbus-eyebrow');
    const heading = root.querySelector('h2');
    const body  = root.querySelector('.nb-ebook__body, p');
    const cta   = root.querySelector('.sb-btn, .nimbus-btn, a[class*="btn"]');

    mm.add(MM.reduced, () => {});
    mm.add(ALL, () => {
      if (cover) {
        gsap.set(cover, { opacity: 0, scale: 0.9, y: 40, rotation: -2 });
        gsap.to(cover, {
          opacity: 1, scale: 1, y: 0, rotation: 0,
          duration: 1.2, ease: 'expo.out',
          scrollTrigger: st(cover, 'top 82%')
        });
      }
      if (eyebrow) R().letterTrack(eyebrow, { duration: 0.95, from: '0.4em' });
      if (heading) R().wordMask(heading, { duration: 1.0, stagger: 0.07 });
      if (body) {
        gsap.set(body, { opacity: 0, y: 14 });
        gsap.to(body, {
          opacity: 1, y: 0, duration: 0.8, ease: 'power3.out', delay: 0.1,
          scrollTrigger: st(body, 'top 86%')
        });
      }
      if (cta) R().scalePop(cta, { duration: 0.7, from: 0.92 });
    });
  });

  /* ═══════════════════════════════════════════════════════════════
   * HEADER — no-op so the engine doesn't apply legacy reveals to it.
   * ═══════════════════════════════════════════════════════════════ */
  NM.register('header', function () {});


  /* ═══════════════════════════════════════════════════════════════
   * WL-HERO — above-fold cinematic: eyebrow letter-track, heading
   * word-mask, date items cascade up, form card scale-settle,
   * watermark drifts on scroll.
   * ═══════════════════════════════════════════════════════════════ */
  NM.register('wl-hero', function (root, mm) {
    var eyebrow   = root.querySelector('.nwl-hero__eyebrow');
    var heading   = root.querySelector('.nwl-hero__heading');
    var dateItems = root.querySelectorAll('.nwl-hero__date-item');
    var formSide  = root.querySelector('.nwl-hero__form-side');
    var watermark = root.querySelector('.nwl-hero__watermark');
    var above     = aboveFold(root);

    mm.add(MM.reduced, function () {});

    mm.add(ALL, function () {
      var tl = gsap.timeline({
        scrollTrigger: above ? null : st(root, 'top 90%'),
        delay: above ? 0.1 : 0
      });

      if (eyebrow) R().letterTrack(eyebrow, { timeline: tl, position: 0.05, duration: 1.0, from: '0.4em' });
      if (heading) R().wordMask(heading, { timeline: tl, position: 0.15, duration: 1.1, stagger: 0.07 });

      if (dateItems.length) {
        gsap.set([].slice.call(dateItems), { opacity: 0, y: 20 });
        tl.to([].slice.call(dateItems), {
          opacity: 1, y: 0, duration: 0.75, stagger: 0.09, ease: 'power3.out'
        }, '-=0.5');
      }

      if (formSide) {
        gsap.set(formSide, { opacity: 0, x: 36, scale: 0.97 });
        tl.to(formSide, {
          opacity: 1, x: 0, scale: 1, duration: 1.0, ease: 'expo.out'
        }, '-=0.6');
      }
    });

    mm.add(WIDE, function () {
      if (!watermark) return;
      gsap.to(watermark, {
        y: -80, ease: 'none',
        scrollTrigger: { trigger: root, start: 'top top', end: 'bottom top', scrub: 0.8 }
      });
    });
  });

  /* ═══════════════════════════════════════════════════════════════
   * WL-EARLY — ghost number scale-settle, eyebrow track, heading
   * word-mask, body fade, perk cards stagger-depth + tilt.
   * ═══════════════════════════════════════════════════════════════ */
  NM.register('wl-early', function (root, mm) {
    var num     = root.querySelector('.nwl-early__num');
    var eyebrow = root.querySelector('.nwl-early__eyebrow');
    var heading = root.querySelector('.nwl-early__heading');
    var body    = root.querySelector('.nwl-early__body');
    var perks   = root.querySelectorAll('.nwl-perk');

    mm.add(MM.reduced, function () {});

    mm.add(ALL, function () {
      if (num) {
        gsap.set(num, { opacity: 0, scale: 1.18 });
        gsap.to(num, {
          opacity: 1, scale: 1, duration: 1.4, ease: 'expo.out',
          scrollTrigger: st(num, 'top 88%')
        });
      }
      if (eyebrow) R().letterTrack(eyebrow, { duration: 0.95, from: '0.4em' });
      if (heading) R().wordMask(heading, { duration: 1.05, stagger: 0.07 });
      if (body) {
        gsap.set(body, { opacity: 0, y: 16 });
        gsap.to(body, {
          opacity: 1, y: 0, duration: 0.85, ease: 'power3.out', delay: 0.1,
          scrollTrigger: st(body, 'top 86%')
        });
      }
      if (perks.length) R().staggerDepth([].slice.call(perks), { stagger: 0.1, y: 50 });
    });

    mm.add(WIDE, function () {
      var cleanups = [].slice.call(perks)
        .map(function (card) { return R().magneticTilt(card, { max: 4, lift: 2 }); })
        .filter(Boolean);
      return function () { cleanups.forEach(function (fn) { fn(); }); };
    });
  });

  /* ═══════════════════════════════════════════════════════════════
   * WL-TOPICS — eyebrow track, heading word-mask, intro fade, topic
   * rows slide in from left with number pink flash on arrival.
   * ═══════════════════════════════════════════════════════════════ */
  NM.register('wl-topics', function (root, mm) {
    var eyebrow = root.querySelector('.nwl-topics__eyebrow');
    var heading = root.querySelector('.nwl-topics__heading');
    var intro   = root.querySelector('.nwl-topics__intro');
    var topics  = root.querySelectorAll('.nwl-topic');
    var nums    = root.querySelectorAll('.nwl-topic__num');

    mm.add(MM.reduced, function () {});

    mm.add(ALL, function () {
      if (eyebrow) R().letterTrack(eyebrow, { duration: 0.95, from: '0.4em' });
      if (heading) R().wordMask(heading, { duration: 1.05, stagger: 0.07 });
      if (intro) {
        gsap.set(intro, { opacity: 0, y: 14 });
        gsap.to(intro, {
          opacity: 1, y: 0, duration: 0.8, ease: 'power3.out', delay: 0.1,
          scrollTrigger: st(intro, 'top 86%')
        });
      }
      if (topics.length) {
        gsap.set([].slice.call(topics), { opacity: 0, x: -36 });
        gsap.to([].slice.call(topics), {
          opacity: 1, x: 0, duration: 0.75, stagger: 0.08, ease: 'power3.out',
          scrollTrigger: st(topics[0].parentElement, 'top 84%'),
          onComplete: function () {
            if (nums.length) {
              gsap.from([].slice.call(nums), {
                color: 'var(--p, #c49a6c)', duration: 0.6, stagger: 0.06, ease: 'power2.out'
              });
            }
          }
        });
      }
    });
  });

  /* ═══════════════════════════════════════════════════════════════
   * WL-DISCOVER — eyebrow track, heading word-mask, cards stagger
   * depth cascade with magnetic tilt on desktop.
   * ═══════════════════════════════════════════════════════════════ */
  NM.register('wl-discover', function (root, mm) {
    var eyebrow = root.querySelector('.nwl-discover__eyebrow');
    var heading = root.querySelector('.nwl-discover__heading');
    var cards   = root.querySelectorAll('.nwl-card');

    mm.add(MM.reduced, function () {});

    mm.add(ALL, function () {
      if (eyebrow) R().letterTrack(eyebrow, { duration: 0.95, from: '0.4em' });
      if (heading) R().wordMask(heading, { duration: 1.05, stagger: 0.07 });
      if (cards.length) R().staggerDepth([].slice.call(cards), { stagger: 0.09, y: 56 });
    });

    mm.add(WIDE, function () {
      var cleanups = [].slice.call(cards)
        .map(function (card) { return R().magneticTilt(card, { max: 5, lift: 3 }); })
        .filter(Boolean);
      return function () { cleanups.forEach(function (fn) { fn(); }); };
    });
  });

  /* ═══════════════════════════════════════════════════════════════
   * WL-FOR-WHO — eyebrow track, heading word-mask, lead body fade,
   * persona items stagger from right.
   * ═══════════════════════════════════════════════════════════════ */
  NM.register('wl-for-who', function (root, mm) {
    var eyebrow = root.querySelector('.nwl-for__eyebrow');
    var heading = root.querySelector('.nwl-for__heading');
    var lead    = root.querySelector('.nwl-for__lead');
    var items   = root.querySelectorAll('.nwl-for-item');

    mm.add(MM.reduced, function () {});

    mm.add(ALL, function () {
      if (eyebrow) R().letterTrack(eyebrow, { duration: 0.95, from: '0.4em' });
      if (heading) R().wordMask(heading, { duration: 1.05, stagger: 0.07 });
      if (lead) {
        gsap.set(lead, { opacity: 0, y: 14 });
        gsap.to(lead, {
          opacity: 1, y: 0, duration: 0.8, ease: 'power3.out', delay: 0.1,
          scrollTrigger: st(lead, 'top 86%')
        });
      }
      if (items.length) {
        gsap.set([].slice.call(items), { opacity: 0, x: 28 });
        gsap.to([].slice.call(items), {
          opacity: 1, x: 0, duration: 0.7, stagger: 0.07, ease: 'power3.out',
          scrollTrigger: st(items[0].parentElement, 'top 84%')
        });
      }
    });
  });

  /* ═══════════════════════════════════════════════════════════════
   * WL-BRAND — editorial split. Image clip-reveal with counter-
   * parallax inner; eyebrow track; heading word-mask; body lines
   * fade up; blockquote slides in from left.
   * ═══════════════════════════════════════════════════════════════ */
  NM.register('wl-brand', function (root, mm) {
    var eyebrow = root.querySelector('.nwl-brand__eyebrow');
    var heading = root.querySelector('.nwl-brand__heading');
    var bodies  = root.querySelectorAll('.nwl-brand__body');
    var quote   = root.querySelector('.nwl-brand__quote');
    var img     = root.querySelector('.nwl-brand__img');

    mm.add(MM.reduced, function () {});

    mm.add(ALL, function () {
      if (img)     R().imageReveal(img, { duration: 1.5, counter: true });
      if (eyebrow) R().letterTrack(eyebrow, { duration: 0.95, from: '0.4em' });
      if (heading) R().wordMask(heading, { duration: 1.05, stagger: 0.07 });
      if (bodies.length) {
        gsap.set([].slice.call(bodies), { opacity: 0, y: 18 });
        gsap.to([].slice.call(bodies), {
          opacity: 1, y: 0, duration: 0.8, stagger: 0.1, ease: 'power3.out', delay: 0.1,
          scrollTrigger: st(bodies[0], 'top 86%')
        });
      }
      if (quote) {
        gsap.set(quote, { opacity: 0, x: -32 });
        gsap.to(quote, {
          opacity: 1, x: 0, duration: 1.0, ease: 'expo.out', delay: 0.15,
          scrollTrigger: st(quote, 'top 88%')
        });
      }
    });

    mm.add(WIDE, function () {
      if (!img) return;
      var inner = img.querySelector('img, picture') || img;
      gsap.to(inner, {
        yPercent: -8, ease: 'none',
        scrollTrigger: { trigger: img, start: 'top bottom', end: 'bottom top', scrub: 0.6 }
      });
    });
  });

  /* ═══════════════════════════════════════════════════════════════
   * WL-CTA — centred dark block. Eyebrow track, heading word-mask,
   * sub fade, form wrap clip-reveal from bottom.
   * ═══════════════════════════════════════════════════════════════ */
  NM.register('wl-cta', function (root, mm) {
    var eyebrow  = root.querySelector('.nwl-cta__eyebrow');
    var heading  = root.querySelector('.nwl-cta__heading');
    var sub      = root.querySelector('.nwl-cta__sub');
    var formWrap = root.querySelector('.nwl-cta__form-wrap');

    mm.add(MM.reduced, function () {});

    mm.add(ALL, function () {
      var tl = gsap.timeline({ scrollTrigger: st(root, 'top 82%') });
      if (eyebrow) R().letterTrack(eyebrow, { timeline: tl, position: 0,   duration: 0.95, from: '0.4em' });
      if (heading) R().wordMask(heading,    { timeline: tl, position: 0.1, duration: 1.05, stagger: 0.07 });
      if (sub) {
        gsap.set(sub, { opacity: 0, y: 14 });
        tl.to(sub, { opacity: 1, y: 0, duration: 0.8, ease: 'power3.out' }, '-=0.5');
      }
      if (formWrap) {
        gsap.set(formWrap, { clipPath: 'inset(100% 0 0 0)' });
        tl.to(formWrap, { clipPath: 'inset(0% 0 0 0)', duration: 1.0, ease: 'expo.out' }, '-=0.4');
      }
    });
  });

  /* ═══════════════════════════════════════════════════════════════
   * WL-FAQ — sticky sidebar: eyebrow track, heading word-mask, sub
   * fade, accordion items cascade from left.
   * ═══════════════════════════════════════════════════════════════ */
  NM.register('wl-faq', function (root, mm) {
    var eyebrow = root.querySelector('.nwl-faq__eyebrow');
    var heading = root.querySelector('.nwl-faq__heading');
    var sub     = root.querySelector('.nwl-faq__sub');
    var items   = root.querySelectorAll('.nwl-faq-item');

    mm.add(MM.reduced, function () {});

    mm.add(ALL, function () {
      if (eyebrow) R().letterTrack(eyebrow, { duration: 0.95, from: '0.4em' });
      if (heading) R().wordMask(heading, { duration: 1.05, stagger: 0.07 });
      if (sub) {
        gsap.set(sub, { opacity: 0, y: 12 });
        gsap.to(sub, {
          opacity: 1, y: 0, duration: 0.8, ease: 'power3.out', delay: 0.1,
          scrollTrigger: st(sub, 'top 86%')
        });
      }
      if (items.length) {
        gsap.set([].slice.call(items), { opacity: 0, x: -28 });
        gsap.to([].slice.call(items), {
          opacity: 1, x: 0, duration: 0.7, stagger: 0.06, ease: 'power3.out',
          scrollTrigger: st(items[0].parentElement, 'top 86%')
        });
      }
    });
  });


  /* ═══════════════════════════════════════════════════════════════
   * SALON-LOCATOR — hero: eyebrow letter-track, heading word-mask,
   * sub fade, hint pill scale-pop, watermark parallax drift.
   * Perks: stagger-depth + tilt. CTA: word-mask + button pop.
   * ═══════════════════════════════════════════════════════════════ */
  NM.register('salon-locator', function (root, mm) {
    /* ── Hero ── */
    var eyebrow   = root.querySelector('.nsl-hero__eyebrow');
    var heading   = root.querySelector('.nsl-hero__heading');
    var sub       = root.querySelector('.nsl-hero__sub');
    var ctas      = root.querySelector('.nsl-hero__ctas');
    var watermark = root.querySelector('.nsl-hero__watermark');
    /* ── Steps ── */
    var stepsEye  = root.querySelector('.nsl-steps__eyebrow');
    var stepsHead = root.querySelector('.nsl-steps__heading');
    var steps     = root.querySelectorAll('.nsl-step');
    /* ── Plans ── */
    var plansEye  = root.querySelector('.nsl-plans__eyebrow');
    var plansHead = root.querySelector('.nsl-plans__heading');
    var plansSub  = root.querySelector('.nsl-plans__sub');
    var plans     = root.querySelectorAll('.nsl-plan');
    /* ── Apply ── */
    var applyEye  = root.querySelector('.nsl-apply__eyebrow');
    var applyHead = root.querySelector('.nsl-apply__heading');
    var applySub  = root.querySelector('.nsl-apply__sub');
    var applyNote = root.querySelector('.nsl-apply__note');
    var applyForm = root.querySelector('.nsl-apply__form');
    /* ── Perks ── */
    var perksEye  = root.querySelector('.nsl-perks__eyebrow');
    var perksHead = root.querySelector('.nsl-perks__heading');
    var perks     = root.querySelectorAll('.nsl-perk');
    var above     = aboveFold(root);

    mm.add(MM.reduced, function () {});

    mm.add(ALL, function () {
      /* ─ Hero — sequenced timeline ─ */
      var tl = gsap.timeline({
        scrollTrigger: above ? null : st(root, 'top 90%'),
        delay: above ? 0.1 : 0
      });
      if (eyebrow) R().letterTrack(eyebrow, { timeline: tl, position: 0.05, duration: 1.0, from: '0.4em' });
      if (heading) R().wordMask(heading, { timeline: tl, position: 0.15, duration: 1.1, stagger: 0.07 });
      if (sub) {
        gsap.set(sub, { opacity: 0, y: 18 });
        tl.to(sub, { opacity: 1, y: 0, duration: 0.85, ease: 'power3.out' }, '-=0.55');
      }
      if (ctas) R().scalePop(ctas, { timeline: tl, position: '-=0.4', duration: 0.75, from: 0.92 });

      /* ─ Steps — slide in from left ─ */
      if (stepsEye)  R().letterTrack(stepsEye, { duration: 0.95, from: '0.4em' });
      if (stepsHead) R().wordMask(stepsHead, { duration: 1.05, stagger: 0.07 });
      if (steps.length) {
        gsap.set([].slice.call(steps), { opacity: 0, x: -32 });
        gsap.to([].slice.call(steps), {
          opacity: 1, x: 0, duration: 0.75, stagger: 0.1, ease: 'power3.out',
          scrollTrigger: st(steps[0].parentElement, 'top 84%')
        });
      }

      /* ─ Plans — stagger-depth ─ */
      if (plansEye)  R().letterTrack(plansEye, { duration: 0.95, from: '0.4em' });
      if (plansHead) R().wordMask(plansHead, { duration: 1.05, stagger: 0.07 });
      if (plansSub) {
        gsap.set(plansSub, { opacity: 0, y: 14 });
        gsap.to(plansSub, {
          opacity: 1, y: 0, duration: 0.8, ease: 'power3.out', delay: 0.1,
          scrollTrigger: st(plansSub, 'top 86%')
        });
      }
      if (plans.length) R().staggerDepth([].slice.call(plans), { stagger: 0.12, y: 60 });

      /* ─ Apply form — left column then form clip-reveal ─ */
      if (applyEye)  R().letterTrack(applyEye, { duration: 0.95, from: '0.4em' });
      if (applyHead) R().wordMask(applyHead, { duration: 1.05, stagger: 0.07 });
      if (applySub) {
        gsap.set(applySub, { opacity: 0, y: 14 });
        gsap.to(applySub, {
          opacity: 1, y: 0, duration: 0.8, ease: 'power3.out', delay: 0.1,
          scrollTrigger: st(applySub, 'top 86%')
        });
      }
      if (applyNote) {
        gsap.set(applyNote, { opacity: 0, x: -24 });
        gsap.to(applyNote, {
          opacity: 1, x: 0, duration: 0.9, ease: 'expo.out', delay: 0.15,
          scrollTrigger: st(applyNote, 'top 88%')
        });
      }
      if (applyForm) {
        gsap.set(applyForm, { clipPath: 'inset(0 0 100% 0)' });
        gsap.to(applyForm, {
          clipPath: 'inset(0 0 0% 0)', duration: 1.1, ease: 'expo.out', delay: 0.1,
          scrollTrigger: st(applyForm, 'top 82%')
        });
      }

      /* ─ Perks — stagger-depth ─ */
      if (perksEye)  R().letterTrack(perksEye, { duration: 0.95, from: '0.4em' });
      if (perksHead) R().wordMask(perksHead, { duration: 1.05, stagger: 0.07 });
      if (perks.length) R().staggerDepth([].slice.call(perks), { stagger: 0.09, y: 48 });
    });

    /* Watermark parallax + plan tilt on desktop */
    mm.add(WIDE, function () {
      if (watermark) {
        gsap.to(watermark, {
          y: -60, ease: 'none',
          scrollTrigger: { trigger: root, start: 'top top', end: 'bottom top', scrub: 0.8 }
        });
      }
      var cleanups = [].slice.call(perks)
        .map(function (card) { return R().magneticTilt(card, { max: 4, lift: 2 }); })
        .filter(Boolean);
      return function () { cleanups.forEach(function (fn) { fn(); }); };
    });
  });

  /* ═══════════════════════════════════════════════════════════════
   * SALON-APPLY — hero: eyebrow letter-track, heading word-mask,
   * sub fade, chip scale-pop. Steps: stagger in. Panel: depth reveal.
   * ═══════════════════════════════════════════════════════════════ */
  NM.register('salon-apply', function (root, mm) {
    var eyebrow = root.querySelector('.nsa-hero__eyebrow');
    var heading = root.querySelector('.nsa-hero__heading');
    var sub     = root.querySelector('.nsa-hero__sub');
    var chip    = root.querySelector('.nsa-hero__chip');
    var steps   = root.querySelectorAll('.nsa-step');
    var panel   = root.querySelector('.nsa-panel');
    var above   = aboveFold(root);

    mm.add(MM.reduced, function () {});

    mm.add(ALL, function () {
      var tl = gsap.timeline({
        scrollTrigger: above ? null : st(root, 'top 90%'),
        delay: above ? 0.1 : 0
      });
      if (eyebrow) R().letterTrack(eyebrow, { timeline: tl, position: 0.05, duration: 1.0, from: '0.4em' });
      if (heading) R().wordMask(heading, { timeline: tl, position: 0.15, duration: 1.1, stagger: 0.07 });
      if (sub) {
        gsap.set(sub, { opacity: 0, y: 18 });
        tl.to(sub, { opacity: 1, y: 0, duration: 0.85, ease: 'power3.out' }, '-=0.55');
      }
      if (chip) R().scalePop(chip, { timeline: tl, position: '-=0.4', duration: 0.7, from: 0.92 });

      if (steps.length) {
        gsap.set([].slice.call(steps), { opacity: 0, y: 18 });
        gsap.to([].slice.call(steps), {
          opacity: 1, y: 0, duration: 0.7, stagger: 0.09, ease: 'power3.out',
          scrollTrigger: st(steps[0].parentElement, 'top 88%')
        });
      }

      if (panel) R().staggerDepth([panel], { stagger: 0, y: 48 });
    });
  });

})();
